/**
 * VideoMeetingOverlay
 *
 * Transparante overlay boven het Jitsi-scherm die toont:
 *  - Club-logo en/of lid-avatar (afwisselend op basis van organisator-instellingen)
 *  - Afteller (groot, kleur instelbaar)
 *  - NR: [x] (volgorde-nummer, rechtsboven)
 *  - NEXT-thumbnail (volgende spreker)
 *  - Full-width banner onderin (bannerkleur + inhoud instelbaar)
 *
 * Ontvangt alle data als props — geen eigen Firestore-queries.
 */

import React, { useEffect, useRef, useState } from 'react';

export interface OverlaySettings {
  bannerColor:           string;
  bannerTextColor:       string;
  timerColor:            string;
  numberColor:           string;
  logoMode:              'club' | 'member' | 'alternating';
  logoSwitchIntervalSec: number;
  logoFadeDurationSec:   number;
  bannerContent:         'searchQuestion' | 'nameCompany' | 'message' | 'title';
  numberPrefix:          string;
}

export interface OverlayEntry {
  userId:         string;
  presenterName:  string;
  slotNumber:     number;
  photoURL?:      string;
  searchQuestion?: string;
  message?:       string;
  title?:         string;
  company?:       string;
  enabled:        boolean;
}

interface VideoMeetingOverlayProps {
  /** Toont de overlay alleen als er een actieve spreker is */
  isActive:           boolean;
  currentEntry:       OverlayEntry | null;
  nextEntry:          OverlayEntry | null;
  remainingSeconds:   number;
  totalSeconds:       number;
  clubLogoUrl?:       string;
  settings:           Partial<OverlaySettings>;
}

const DEFAULTS: OverlaySettings = {
  bannerColor:           '#1D4ED8',
  bannerTextColor:       '#FFFFFF',
  timerColor:            '#FFFFFF',
  numberColor:           '#FFFFFF',
  logoMode:              'alternating',
  logoSwitchIntervalSec: 10,
  logoFadeDurationSec:   1.5,
  bannerContent:         'searchQuestion',
  numberPrefix:          'NR:',
};

function merge(settings: Partial<OverlaySettings>): OverlaySettings {
  return { ...DEFAULTS, ...settings };
}

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s} SEC`;
}

const VideoMeetingOverlay: React.FC<VideoMeetingOverlayProps> = ({
  isActive,
  currentEntry,
  nextEntry,
  remainingSeconds,
  totalSeconds,
  clubLogoUrl,
  settings,
}) => {
  const cfg = merge(settings);

  // ── Logo fade-wisseling ──────────────────────────────────────────────────
  const [showClubLogo, setShowClubLogo] = useState(true);
  const [logoOpacity,  setLogoOpacity]  = useState(1);
  const fadeRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (cfg.logoMode !== 'alternating') {
      setShowClubLogo(cfg.logoMode === 'club');
      setLogoOpacity(1);
      return;
    }
    if (!isActive) return;

    const intervalMs  = cfg.logoSwitchIntervalSec  * 1000;
    const fadeDurMs   = cfg.logoFadeDurationSec     * 1000;
    const halfFade    = fadeDurMs / 2;

    const cycle = () => {
      // Fade out
      setLogoOpacity(0);
      setTimeout(() => {
        setShowClubLogo(prev => !prev);
        // Fade in
        setLogoOpacity(1);
      }, halfFade);
    };

    fadeRef.current = setInterval(cycle, intervalMs);
    return () => { if (fadeRef.current) clearInterval(fadeRef.current); };
  }, [cfg.logoMode, cfg.logoSwitchIntervalSec, cfg.logoFadeDurationSec, isActive]);

  if (!isActive || !currentEntry) return null;

  // ── Welke logo wordt getoond ──────────────────────────────────────────────
  const activeLogo = showClubLogo
    ? (clubLogoUrl || null)
    : (currentEntry.photoURL || null);

  const activeLogoInitial = showClubLogo
    ? 'C'
    : (currentEntry.presenterName[0]?.toUpperCase() || '?');

  // ── Banner tekst ──────────────────────────────────────────────────────────
  const bannerText = (() => {
    switch (cfg.bannerContent) {
      case 'searchQuestion': return currentEntry.searchQuestion ? `ZOEK VRAAG: ${currentEntry.searchQuestion.toUpperCase()}` : null;
      case 'nameCompany':    return [currentEntry.presenterName, currentEntry.company].filter(Boolean).join(' — ').toUpperCase();
      case 'message':        return currentEntry.message?.toUpperCase() || null;
      case 'title':          return currentEntry.title?.toUpperCase() || null;
      default:               return null;
    }
  })();

  // ── Voortgang percentage ──────────────────────────────────────────────────
  const progressPct = totalSeconds > 0
    ? Math.max(0, Math.min(100, (remainingSeconds / totalSeconds) * 100))
    : 0;

  const fadeDurationCss = `${cfg.logoFadeDurationSec}s`;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        pointerEvents: 'none', // Jitsi blijft klikbaar
        overflow: 'hidden',
      }}
    >
      {/* ── Logo + Timer (linksboven) ────────────────────────────────────── */}
      <div style={{ position: 'absolute', top: 16, left: 16 }}>
        {/* Logo */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            overflow: 'hidden',
            background: cfg.bannerColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
            opacity: logoOpacity,
            transition: `opacity ${fadeDurationCss} ease`,
          }}
        >
          {activeLogo ? (
            <img
              src={activeLogo}
              alt="logo"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <span style={{ fontSize: 20, fontWeight: 900, color: cfg.bannerTextColor }}>
              {activeLogoInitial}
            </span>
          )}
        </div>

        {/* Afteller */}
        <div
          style={{
            marginTop: 6,
            fontSize: 20,
            fontWeight: 900,
            color: remainingSeconds <= 10 ? '#EF4444' : cfg.timerColor,
            textShadow: '0 1px 6px rgba(0,0,0,0.6)',
            letterSpacing: 1,
            transition: 'color 0.3s ease',
          }}
        >
          {formatTime(remainingSeconds)}
        </div>

        {/* Voortgangsbalk */}
        <div style={{ width: 56, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 4, marginTop: 4, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${progressPct}%`,
              background: remainingSeconds <= 10 ? '#EF4444' : cfg.timerColor,
              borderRadius: 4,
              transition: 'width 1s linear, background 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* ── NEXT + NR (rechtsboven) ──────────────────────────────────────── */}
      <div style={{ position: 'absolute', top: 16, right: 16, textAlign: 'right' }}>
        {nextEntry && (
          <>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.75)', letterSpacing: 1, marginBottom: 4 }}>
              NEXT
            </div>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                border: '1.5px solid rgba(255,255,255,0.2)',
              }}
            >
              {nextEntry.photoURL ? (
                <img src={nextEntry.photoURL} alt={nextEntry.presenterName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <span style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>
                  {nextEntry.presenterName[0]?.toUpperCase() || '?'}
                </span>
              )}
            </div>
            <div style={{ fontSize: 10, fontWeight: 800, color: cfg.numberColor, marginTop: 3, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
              {cfg.numberPrefix} {nextEntry.slotNumber}
            </div>
          </>
        )}

        {/* Huidige spreker NR — altijd zichtbaar */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: cfg.numberColor,
            marginTop: nextEntry ? 8 : 0,
            textShadow: '0 1px 6px rgba(0,0,0,0.6)',
            background: 'rgba(0,0,0,0.35)',
            borderRadius: 8,
            padding: '3px 8px',
            display: 'inline-block',
          }}
        >
          {cfg.numberPrefix} {currentEntry.slotNumber}
        </div>
      </div>

      {/* ── Banner onderin — 80px omhoog zodat Jitsi-toolbar vrij blijft ── */}
      {bannerText && (
        <div
          style={{
            position: 'absolute',
            bottom: 80,   /* Jitsi toolbar is ~76px hoog */
            left: 0,
            right: 0,
            background: cfg.bannerColor,
            padding: '10px 20px',
            boxShadow: '0 -2px 16px rgba(0,0,0,0.25)',
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              color: cfg.bannerTextColor,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {bannerText}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoMeetingOverlay;
